// Small IndexedDB-backed cache for API responses with a TTL.
// Every call fails soft: if IndexedDB is unavailable (private mode, jsdom, quota),
// reads resolve to null and writes are no-ops, so callers fall back to the network.

const DB_NAME = "webportal-cache";
const STORE_NAME = "apiCache";
const DB_VERSION = 1;

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "key" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
  return dbPromise;
};

const runRequest = async (mode, operation) => {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const store = db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
      const request = operation(store);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
};

// Resolves to { value, storedAt } while the entry is fresh, otherwise null.
export const getCached = async (key, ttl) => {
  const entry = await runRequest("readonly", (store) => store.get(key));
  if (!entry) return null;
  if (Date.now() - entry.storedAt >= ttl) {
    await runRequest("readwrite", (store) => store.delete(key));
    return null;
  }
  return { value: entry.value, storedAt: entry.storedAt };
};

export const setCached = (key, value) =>
  runRequest("readwrite", (store) => store.put({ key, value, storedAt: Date.now() }));

const memoryClearCallbacks = [];

// Lets in-memory layers built on top of this cache reset together with IndexedDB.
export const onApiCacheClear = (callback) => {
  memoryClearCallbacks.push(callback);
};

export const clearApiCache = () => {
  memoryClearCallbacks.forEach((callback) => callback());
  return runRequest("readwrite", (store) => store.clear());
};
